// client/src/pages/ViewAllData.js
import React, { useState, useEffect } from 'react';
import axios from 'axios';
import { useAuth } from '../contexts/AuthContext';
import { useNavigate } from 'react-router-dom';
import { useToast } from '../components/Toast';
import { useConfirm } from '../components/ConfirmModal';
import { colors } from '../theme/tokens';

const ViewAllData = () => {
  const { token, canApprove } = useAuth();
  const navigate = useNavigate();
  const showToast = useToast();
  const confirm = useConfirm();
  
  const [makes, setMakes] = useState([]);
  const [models, setModels] = useState([]);
  const [variants, setVariants] = useState([]);
  
  const [selectedMake, setSelectedMake] = useState('');
  const [selectedModel, setSelectedModel] = useState('');
  const [selectedVariant, setSelectedVariant] = useState(null);
  
  const [loading, setLoading] = useState(false);
  const [savingPart, setSavingPart] = useState(false);
  const [deletingPart, setDeletingPart] = useState(null);
  const [editingPart, setEditingPart] = useState(null);
  const [editValues, setEditValues] = useState({});

  const API_URL = process.env.REACT_APP_API_URL || 'http://localhost:5000';

  useEffect(() => {
    if (!canApprove) {
      navigate('/');
      return;
    }
    fetchMakes();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [canApprove, navigate]);

  const fetchMakes = async () => {
    try {
      setLoading(true);
      const response = await axios.get(`${API_URL}/api/data-management/makes`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      setMakes(response.data);
    } catch (err) {
      showToast('Failed to load makes: ' + (err.response?.data?.error || err.message), 'error');
    } finally {
      setLoading(false);
    }
  };

  const fetchModels = async (make) => {
    try {
      setLoading(true);
      const response = await axios.get(`${API_URL}/api/data-management/models/${make}`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      setModels(response.data);
      setVariants([]);
      setSelectedVariant(null);
    } catch (err) {
      showToast('Failed to load models: ' + (err.response?.data?.error || err.message), 'error');
    } finally {
      setLoading(false);
    }
  };

  const fetchVariants = async (make, model) => {
    try {
      setLoading(true);
      const response = await axios.get(`${API_URL}/api/data-management/variants/${make}/${model}`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      setVariants(response.data);
      setSelectedVariant(null);
    } catch (err) {
      showToast('Failed to load variants: ' + (err.response?.data?.error || err.message), 'error');
    } finally {
      setLoading(false);
    }
  };

  const handleMakeChange = (make) => {
    setSelectedMake(make);
    setSelectedModel('');
    setModels([]);
    setVariants([]);
    setSelectedVariant(null);
    if (make) {
      fetchModels(make);
    }
  };

  const handleModelChange = (model) => {
    setSelectedModel(model);
    setVariants([]);
    setSelectedVariant(null);
    if (model && selectedMake) {
      fetchVariants(selectedMake, model);
    }
  };

  const startEdit = (carId, part) => {
    setEditingPart(`${carId}-${part._id}`);
    setEditValues({
      position: part.position,
      code: part.code,
      quantity: part.quantity,
      unit: part.unit
    });
  };

  const cancelEdit = () => {
    setEditingPart(null);
    setEditValues({});
  };

  const saveEdit = async (carId, partId) => {
    setSavingPart(true);
    try {
      await axios.patch(
        `${API_URL}/api/data-management/part/${carId}/${partId}`,
        editValues,
        { headers: { Authorization: `Bearer ${token}` } }
      );
      await fetchVariants(selectedMake, selectedModel);
      setEditingPart(null);
      setEditValues({});
      showToast('Part updated successfully!', 'success');
    } catch (err) {
      showToast('Failed to update part: ' + (err.response?.data?.error || err.message), 'error');
    } finally {
      setSavingPart(false);
    }
  };

  const deletePart = async (carId, partId) => {
    const ok = await confirm('Are you sure you want to delete this part? This action cannot be undone.', {
      title: 'Delete Part',
      icon: '🗑️',
      confirmText: 'Delete',
      danger: true
    });
    if (!ok) return;

    setDeletingPart(partId);
    try {
      await axios.delete(
        `${API_URL}/api/data-management/part/${carId}/${partId}`,
        { headers: { Authorization: `Bearer ${token}` } }
      );
      await fetchVariants(selectedMake, selectedModel);
      showToast('Part deleted successfully!', 'success');
    } catch (err) {
      showToast('Failed to delete part: ' + (err.response?.data?.error || err.message), 'error');
    } finally {
      setDeletingPart(null);
    }
  };

  const styles = {
    container: {
      padding: '20px',
      maxWidth: '1400px',
      margin: '0 auto',
      minHeight: '100vh'
    },
    header: {
      marginBottom: '30px',
      textAlign: 'center'
    },
    title: {
      fontSize: '32px',
      color: colors.parGreen,
      marginBottom: '10px'
    },
    subtitle: {
      fontSize: '16px',
      color: colors.text
    },
    filtersCard: {
      backgroundColor: colors.surface,
      color: colors.text,
      borderRadius: '8px',
      padding: '30px',
      marginBottom: '20px',
      boxShadow: '0 2px 8px rgba(0,0,0,0.1)',
      border: `1px solid ${colors.border}`
    },
    filterRow: {
      display: 'flex',
      gap: '20px',
      marginBottom: '20px',
      flexWrap: 'wrap'
    },
    filterGroup: {
      flex: '1',
      minWidth: '250px'
    },
    label: {
      display: 'block',
      marginBottom: '8px',
      fontWeight: 'bold',
      color: colors.text,
      fontSize: '14px'
    },
    select: {
      width: '100%',
      padding: '12px',
      fontSize: '16px',
      border: `2px solid ${colors.parGreen}`,
      borderRadius: '6px',
      backgroundColor: colors.inputBg,
      color: colors.text,
      cursor: 'pointer'
    },
    variantsGrid: {
      display: 'grid',
      gridTemplateColumns: 'repeat(auto-fill, minmax(300px, 1fr))',
      gap: '20px',
      marginTop: '20px'
    },
    variantCard: {
      backgroundColor: colors.surface,
      color: colors.text,
      borderRadius: '8px',
      padding: '20px',
      boxShadow: '0 2px 8px rgba(0,0,0,0.1)',
      cursor: 'pointer',
      transition: 'transform 0.2s, box-shadow 0.2s',
      border: `2px solid ${colors.parGreen}`
    },
    variantCardHover: {
      transform: 'translateY(-2px)',
      boxShadow: '0 4px 12px rgba(47, 182, 92, 0.3)'
    },
    variantTitle: {
      fontSize: '18px',
      fontWeight: 'bold',
      color: colors.parGreen,
      marginBottom: '10px'
    },
    variantInfo: {
      fontSize: '14px',
      color: colors.textMuted,
      marginBottom: '5px'
    },
    detailsCard: {
      backgroundColor: colors.surface,
      color: colors.text,
      borderRadius: '8px',
      padding: '30px',
      marginTop: '20px',
      boxShadow: '0 2px 8px rgba(0,0,0,0.1)',
      border: `1px solid ${colors.border}`
    },
    detailsHeader: {
      display: 'flex',
      justifyContent: 'space-between',
      alignItems: 'center',
      marginBottom: '20px',
      paddingBottom: '15px',
      borderBottom: `2px solid ${colors.parGreen}`
    },
    detailsTitle: {
      fontSize: '24px',
      fontWeight: 'bold',
      color: colors.parGreen
    },
    backButton: {
      padding: '10px 20px',
      backgroundColor: colors.surface2,
      color: colors.text,
      border: 'none',
      borderRadius: '6px',
      cursor: 'pointer',
      fontSize: '14px'
    },
    partsTable: {
      width: '100%',
      borderCollapse: 'collapse',
      marginTop: '20px'
    },
    tableHeader: {
      backgroundColor: colors.parGreen,
      color: 'white',
      textAlign: 'left'
    },
    th: {
      padding: '12px',
      fontWeight: 'bold',
      fontSize: '14px'
    },
    td: {
      padding: '12px',
      borderBottom: `1px solid ${colors.borderSoft}`,
      fontSize: '14px'
    },
    input: {
      padding: '8px',
      border: `1px solid ${colors.parGreen}`,
      borderRadius: '4px',
      fontSize: '14px',
      width: '100%',
      backgroundColor: colors.inputBg,
      color: colors.text
    },
    actionButtons: {
      display: 'flex',
      gap: '10px'
    },
    editButton: {
      padding: '8px 12px',
      backgroundColor: colors.parLime,
      color: '#1E1E1E',
      border: 'none',
      borderRadius: '4px',
      cursor: 'pointer',
      fontSize: '12px',
      fontWeight: 'bold'
    },
    saveButton: {
      padding: '8px 12px',
      backgroundColor: colors.parGreen,
      color: 'white',
      border: 'none',
      borderRadius: '4px',
      cursor: 'pointer',
      fontSize: '12px',
      fontWeight: 'bold'
    },
    cancelButton: {
      padding: '8px 12px',
      backgroundColor: colors.surface2,
      color: colors.text,
      border: 'none',
      borderRadius: '4px',
      cursor: 'pointer',
      fontSize: '12px',
      fontWeight: 'bold'
    },
    deleteButton: {
      padding: '8px 12px',
      backgroundColor: colors.red,
      color: 'white',
      border: 'none',
      borderRadius: '4px',
      cursor: 'pointer',
      fontSize: '12px',
      fontWeight: 'bold'
    },
    loading: {
      textAlign: 'center',
      padding: '40px',
      fontSize: '18px',
      color: colors.parGreen
    },
    noData: {
      textAlign: 'center',
      padding: '40px',
      fontSize: '16px',
      color: colors.textMuted
    }
  };

  return (
    <div style={styles.container}>
      <div style={styles.header}>
        <h1 style={styles.title}>🗄️ View All Data</h1>
        <p style={styles.subtitle}>Browse, edit, and manage all car parts data</p>
      </div>

      {/* Filters */}
      <div style={styles.filtersCard}>
        <div style={styles.filterRow}>
          {/* Make Dropdown */}
          <div style={styles.filterGroup}>
            <label style={styles.label}>1. Select Make</label>
            <select
              style={styles.select}
              value={selectedMake}
              onChange={(e) => handleMakeChange(e.target.value)}
              disabled={loading}
            >
              <option value="">-- Select Make --</option>
              {makes.map((make) => (
                <option key={make} value={make}>{make}</option>
              ))}
            </select>
          </div>

          {/* Model Dropdown */}
          <div style={styles.filterGroup}>
            <label style={styles.label}>2. Select Model</label>
            <select
              style={styles.select}
              value={selectedModel}
              onChange={(e) => handleModelChange(e.target.value)}
              disabled={!selectedMake || loading}
            >
              <option value="">-- Select Model --</option>
              {models.map((model) => (
                <option key={model} value={model}>{model}</option>
              ))}
            </select>
          </div>
        </div>

        {loading && <div style={styles.loading}>Loading...</div>}
      </div>

      {/* Variants Grid (if model selected but no variant clicked) */}
      {!selectedVariant && variants.length > 0 && (
        <div>
          <h3 style={{ color: colors.text, marginBottom: '15px' }}>
            3. Select Variant ({variants.length} found)
          </h3>
          <div style={styles.variantsGrid}>
            {variants.map((car) => (
              <div
                key={car._id}
                style={styles.variantCard}
                onClick={() => setSelectedVariant(car)}
                onMouseEnter={(e) => {
                  e.currentTarget.style.transform = 'translateY(-2px)';
                  e.currentTarget.style.boxShadow = '0 4px 12px rgba(47, 182, 92, 0.3)';
                }}
                onMouseLeave={(e) => {
                  e.currentTarget.style.transform = 'translateY(0)';
                  e.currentTarget.style.boxShadow = '0 2px 8px rgba(0,0,0,0.1)';
                }}
              >
                <div style={styles.variantTitle}>{car.variant}</div>
                <div style={styles.variantInfo}>📅 {car.yearRange}</div>
                <div style={styles.variantInfo}>🔧 {car.parts.length} parts</div>
              </div>
            ))}
          </div>
        </div>
      )}

      {/* Selected Variant Details */}
      {selectedVariant && (
        <div style={styles.detailsCard}>
          <div style={styles.detailsHeader}>
            <div>
              <div style={styles.detailsTitle}>
                {selectedVariant.make} {selectedVariant.model} - {selectedVariant.variant}
              </div>
              <div style={{ color: colors.textMuted, marginTop: '5px' }}>
                📅 Year Range: {selectedVariant.yearRange} | 🔧 {selectedVariant.parts.length} parts
              </div>
            </div>
            <button
              style={styles.backButton}
              onClick={() => setSelectedVariant(null)}
            >
              ← Back to Variants
            </button>
          </div>

          {selectedVariant.parts.length === 0 ? (
            <div style={styles.noData}>No parts found for this variant.</div>
          ) : (
            <table style={styles.partsTable}>
              <thead style={styles.tableHeader}>
                <tr>
                  <th style={styles.th}>Position</th>
                  <th style={styles.th}>Part Code (SKU)</th>
                  <th style={styles.th}>Quantity</th>
                  <th style={styles.th}>Unit</th>
                  <th style={styles.th}>Status</th>
                  <th style={styles.th}>Added By</th>
                  <th style={styles.th}>Actions</th>
                </tr>
              </thead>
              <tbody>
                {selectedVariant.parts.map((part) => {
                  const isEditing = editingPart === `${selectedVariant._id}-${part._id}`;
                  
                  return (
                    <tr key={part._id}>
                      <td style={styles.td}>
                        {isEditing ? (
                          <input
                            type="text"
                            style={styles.input}
                            value={editValues.position}
                            onChange={(e) => setEditValues({ ...editValues, position: e.target.value })}
                          />
                        ) : (
                          part.position
                        )}
                      </td>
                      <td style={styles.td}>
                        {isEditing ? (
                          <input
                            type="text"
                            style={styles.input}
                            value={editValues.code}
                            onChange={(e) => setEditValues({ ...editValues, code: e.target.value })}
                          />
                        ) : (
                          part.code
                        )}
                      </td>
                      <td style={styles.td}>
                        {isEditing ? (
                          <input
                            type="number"
                            step="0.01"
                            style={styles.input}
                            value={editValues.quantity}
                            onChange={(e) => setEditValues({ ...editValues, quantity: e.target.value })}
                          />
                        ) : (
                          part.quantity
                        )}
                      </td>
                      <td style={styles.td}>
                        {isEditing ? (
                          <input
                            type="text"
                            style={styles.input}
                            value={editValues.unit}
                            onChange={(e) => setEditValues({ ...editValues, unit: e.target.value })}
                          />
                        ) : (
                          part.unit
                        )}
                      </td>
                      <td style={styles.td}>
                        <span style={{
                          padding: '4px 8px',
                          borderRadius: '4px',
                          fontSize: '12px',
                          backgroundColor: part.status === 'approved' ? colors.parGreen : colors.parLime,
                          color: 'white'
                        }}>
                          {part.status}
                        </span>
                      </td>
                      <td style={styles.td}>{part.addedBy}</td>
                      <td style={styles.td}>
                        <div style={styles.actionButtons}>
                          {isEditing ? (
                            <>
                              <button
                                style={{...styles.saveButton, opacity: savingPart ? 0.6 : 1}}
                                onClick={() => saveEdit(selectedVariant._id, part._id)}
                                disabled={savingPart}
                              >
                                {savingPart ? '...' : '💾 Save'}
                              </button>
                              <button
                                style={styles.cancelButton}
                                onClick={cancelEdit}
                                disabled={savingPart}
                              >
                                ✖ Cancel
                              </button>
                            </>
                          ) : (
                            <>
                              <button
                                style={styles.editButton}
                                onClick={() => startEdit(selectedVariant._id, part)}
                              >
                                ✏️ Edit
                              </button>
                              <button
                                style={{...styles.deleteButton, opacity: deletingPart === part._id ? 0.6 : 1}}
                                onClick={() => deletePart(selectedVariant._id, part._id)}
                                disabled={deletingPart === part._id}
                              >
                                {deletingPart === part._id ? '...' : '🗑️ Delete'}
                              </button>
                            </>
                          )}
                        </div>
                      </td>
                    </tr>
                  );
                })}
              </tbody>
            </table>
          )}
        </div>
      )}

      {!loading && variants.length === 0 && selectedModel && (
        <div style={styles.noData}>
          No variants found for {selectedMake} {selectedModel}
        </div>
      )}
    </div>
  );
};

export default ViewAllData;
